import { Component, OnInit } from '@angular/core';

// Materials
import { MatDialog } from '@angular/material/dialog';

// Services
import { MapserviceService } from '../services/map-services.service';
import { UtilitiesService } from '../services/utilities-services.service';
import { PrintService } from '../services/print-services.service';
import { TablesService } from '../services/tables-services.service';

import { environment } from '../../environments/environment';

// Info windows
import { AddStudyComponent, AddElevationComponent, AboutDeleteComponent, AboutStudyDeleteComponent, AboutElevationDeleteComponent } from '../app.component';

// Esri
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import FeatureTable from '@arcgis/core/widgets/FeatureTable';
import Graphic from '@arcgis/core/Graphic';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit {

  // Study search string
  studyFilter: string;

  // Logged in
  loggedIn: boolean;

  // Toggle table
  toggleTable: boolean;
  displayType = 'none';

  // Map
  view;
  map;

  // Query Basins
  queryBasinFeatureLayer; // Basins View Feature Layer
  basinSymbol;
  basinGraphic;
  dowlknumQueryString; // 1a. Query ALL basins in a selected study by dowlknum
  basinGeometries = []; // 2a. Queries ALL basins geometries in a selected study

  // Query Basins (Elevations)
  dowlknumQueryStringElevations; // 1b. Query only selected basins in the elevations table by dowlknum
  basinGeometriesElevations = []; // 2b. Queries only selected basins in the elevations table in a selected study

  // Feature tables
  studiesFeatureTable;
  elevationsFeatureTable;

  // User selected studies
  selectedRow;
  selectedRowProperties;
  previouslySelectedRow = false;
  userSelectedID;
  selectedStudyName;
  userSelectedRecords = [];
  userSelectedDowlknum = [];
  studyQuery;

  // User selected elevations
  userSelectedObjectID = null;
  elevationsSelected = [];
  elevationSelectedBoolean = false;
  elevationsQuery;

  // Study table setup
  studiesTable;
  studiesFields;
  studiesNewRecord;

  // Elevation table setup
  elevationsTable;
  elevationsFields;
  elevationsNewRecord;

  constructor(private mapService: MapserviceService, private utilitiesService: UtilitiesService, private printService: PrintService, private tablesService: TablesService, public dialog: MatDialog) { }

  ngOnInit() {

    /* Map view subscription */
    this.mapService.view.subscribe(result => {
      this.view = result;
    });

    /* Logged in subscription */
    // When toggled, update the table to be viewable (not logged in) -OR- editable (logged in)
    // To reload, the divs need to be cleared of content first
    this.utilitiesService.loggedIn.subscribe(result => {
      this.loggedIn = result;
      // If not logged in, ensure the toggle table is also set to false
      if (result === false) {
       this.utilitiesService.activateTable(false);
       this.userSelectedID = null;
      }
      // Load the tables
      this.loadTables();
    });

    /* Toggle table subscription */
    // When toggled on (true) will display the tables
    // When toggled off (false) will not be displayed
    // See table.component.html for the [ngStyle] class
    this.utilitiesService.toggleTable.subscribe(result => {
      this.toggleTable = result;
      if (result === true) {
        this.displayType = 'block';
        this.utilitiesService.activatePrint(false);
      } else {
        this.displayType = 'none';
      }
      // Note: The following is needed below to ensure tables are updating when adding/deleting records
      // Load feature table properties
      this.studiesTableProperties();
      this.elevationsTableProperties();
      // Load feature tables
      this.loadTables();
    });

  }

  /* Zoom to Basins */
  // Takes the Lake Basin IDs from the Elevations table (dowlknum) and will iterate through to zoom to the extent
  zoomToBasin() {
    // Only run the query if a user has selected a study
    if (this.userSelectedID) {
      // Basins table view
      this.queryBasinFeatureLayer = new FeatureLayer({
        // url: 'https://gis.dnr.state.mn.us/arcgis/rest/services/ewr/app_lfeo_basins_azure_prod/MapServer/0',
        url: environment.basinsLayerUrl,
        outFields: ['*']
      });
      // Clear the query string
      this.dowlknumQueryString = '';
      // If multiple records are in the elevation table, create an "OR" statement for the query string
      // Otherwise if 1 (ONE), or the last record in the table, end the statement
      for (let i = 0; i < this.userSelectedDowlknum.length; i++) {
        if (i + 1 === this.userSelectedDowlknum.length) {
          // tslint:disable-next-line: quotemark
          this.dowlknumQueryString += "dowlknum='" + this.userSelectedDowlknum[i] + "'";
        } else {
          // tslint:disable-next-line: quotemark
          this.dowlknumQueryString += "dowlknum='" + this.userSelectedDowlknum[i] + "' OR ";
        }
      }
      // Basins table view query
      const basinViewQuery = this.queryBasinFeatureLayer.createQuery();
      basinViewQuery.where = this.dowlknumQueryString;
      // Zoom to the basin query extent
      this.queryBasinFeatureLayer.queryExtent(basinViewQuery).then(results => {
        this.view.goTo(results.extent);
      });
      // Basin query features to create graphics layers
      this.queryBasinFeatureLayer.queryFeatures(basinViewQuery).then(results => {
        // Basin graphic symbol
        this.basinSymbol = {
          type: 'simple-fill',
          color: [ 51, 51, 204, 0.9 ],
          style: 'none',
          outline: {
            color: 'blue',
            width: 3
          }
        };
        // Return basin geometries as an array
        this.basinGeometries = results.features.map(feature => {
          return feature.geometry;
        });
        // Add the basin symbol and geometries to a graphic
        // Add the graphic to the map
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < this.basinGeometries.length; i++) {
          // Basin graphics layer
          const basinGraphic = new Graphic({
            geometry: this.basinGeometries[i],
            symbol: this.basinSymbol as any,
          });
          this.view.graphics.add(basinGraphic);
        }
      });
    }
  }

  /* Studies table properties */
  studiesTableProperties() {
    // Study table query parameters
    // IF: a user enters in text into the search string, run a query
    // Note: The query MUST have a lowercase on the field study_name and the string since Esri REST services are case sensitive by default [e.g., LOWER(study_name) LIKE LOWER('%string%')].
    // ELSE: display all records (1=1)
    if (this.studyFilter) {
        // tslint:disable-next-line: quotemark
        this.studyQuery = "LOWER(study_name) LIKE LOWER('%" + this.studyFilter + "%')";
    } else {
      this.studyQuery = '1=1';
    }
    // Studies feature layer parameters for the table
    this.studiesTable = new FeatureLayer({
      // url: 'https://gis.dnr.state.mn.us/arcgis/rest/services/ewr/app_lfeo_table_editor_azure_prod/FeatureServer/13',
      url: environment.studiesTableUrl,
        outFields: ['*'],
        title: 'Studies',
        definitionExpression: this.studyQuery,
    });
    // Study table fields
    this.studiesFields = [
    {
      name: 'study_name',
      label: 'Study Name',
      direction: 'asc'
    },
    {
      name: 'study_remark',
      label: 'Remarks'
    },
    {
      name: 'study_date',
      label: 'Study Date',
      includeTime: false,
    },
    {
      name: 'vertical_datum',
      label: 'Vertical Datum',
    },
    {
      name: 'source_type',
      label: 'Source Type'
    }];
    // Study table actions
    // 1. Add new study
    // 2. Delete existing study
    this.studiesNewRecord = [
      {
        label: 'Add New Study',
        iconClass: 'esri-icon-plus-circled',
        clickFunction: event => {
          return this.addNewTableRecord('study');
        }
      },
      {
        label: 'Delete Study',
        iconClass: 'esri-icon-trash',
        clickFunction: event => {
          return this.removeTableRecord('study');
        }
      }
    ];
  }

  /* Elevations table properties */
  elevationsTableProperties() {
    // Elevations table query parameters
    // If a user selects a feature, run a query on the table
    // Otherwise if all records are shown, show all records
    if (this.userSelectedID) {
      this.elevationsQuery = 'fs_id=' + this.userSelectedID;
      if (this.userSelectedRecords.length === 1) {
        this.userSelectedObjectID = this.userSelectedRecords[0].attributes.objectid;
        this.selectedStudyName = this.userSelectedRecords[0].attributes.study_name;
      } else {
        this.userSelectedObjectID = this.userSelectedRecords[1].attributes.objectid;
        this.selectedStudyName = this.userSelectedRecords[1].attributes.study_name;
      }
    } else {
      this.elevationsQuery = '1=1';
      this.userSelectedObjectID = null;
      this.selectedStudyName = null;
    }
    // Update the selected ID and object ID observables
    this.tablesService.changeUserSelectedID(this.userSelectedID, this.userSelectedObjectID, this.selectedStudyName);
    // Elevations feature layer parameters for the table
    this.elevationsTable = new FeatureLayer({
      // url: 'https://gis.dnr.state.mn.us/arcgis/rest/services/ewr/app_lfeo_table_editor_azure_prod/FeatureServer/14',
      url: environment.elevationsTableUrl,
        outFields: ['*'],
        title: 'Elevations',
       definitionExpression: this.elevationsQuery,
    });
    // Query the elevations table to get the Lake Basin IDs (dowlknum)
    // The query must capture the values as strings so those with leading zeros can be captured
    // If no elevations are present in a study, the map will not zoom to the extent
    this.elevationsTable.queryFeatures().then(response => {
      if (response.features.length) {
        this.userSelectedDowlknum = [];
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < response.features.length; i++) {
         this.userSelectedDowlknum.push(response.features[i].attributes.dowlknum);
        }
        // Once the Lake IDs (dowlknum) have been loaded
        // Zoom to the Basin IDs
        this.zoomToBasin();
      }
    });
    // Elevation table fields
    this.elevationsFields = [
    {
      name: 'dowlknum',
      label: 'Lake Basin ID'
    },
    {
      name: 'pw_basin_name',
      label: 'Lake Basin Name'
    },
    {
      name: 'elevation_feet_10_year_flood',
      label: '10 Year Elevation',
      format: {
        places: 1
      }
    },
    {
      name: 'f10_yr_comment',
      label: '10 Year Comments',
    },
    {
      name: 'elevation_feet_25_year_flood',
      label: '25 Year Elevation',
      format: {
        places: 1
      }
    },
    {
      name: 'f25_yr_comment',
      label: '25 Year Comments'
    },
    {
      name: 'elevation_feet_50_year_flood',
      label: '50 Year Elevation',
      format: {
        places: 1
      }
    },
    {
      name: 'f50_yr_comment',
      label: '50 Year Comments'
    },
    {
      name: 'elevation_feet_100_year_flood',
      label: '100 Year Elevation',
      format: {
        places: 1
      }
    },
    {
      name: 'f100_yr_comment',
      label: '100 Year Comments'
    },
    {
      name: 'elevation_feet_500_year_flood',
      label: '500 Year Elevation',
      format: {
        places: 1
      }
    },
    {
      name: 'f500_yr_comment',
      label: '500 Year Comments'
    }
  ];
  // Elevation table new record action
  // 1. Add elevation: To add an elevation, the user must select a study
  // 2: Delete elevation: Delete options are available at all times.
    if (this.userSelectedID) {
      this.elevationsNewRecord = [
        {
          label: 'Add New Elevation',
          iconClass: 'esri-icon-plus-circled',
          clickFunction: event => {
            return this.addNewTableRecord('elevation');
          }
        },
        {
          label: 'Delete Elevation(s)',
          iconClass: 'esri-icon-trash',
          clickFunction: event => {
            return this.removeTableRecord('elevation');
          }
        }
      ];
    } else {
      this.elevationsNewRecord = [
        {
          label: 'Delete Elevation(s)',
          iconClass: 'esri-icon-trash',
          clickFunction: event => {
            return this.removeTableRecord('elevation');
          }
        }
      ];
    }
  }

  /* Load the tables */
  loadTables() {
    // Load the feature tables
    this.loadFeatureTable(this.studiesTable, this.studiesFields, true, 'studiesTableDiv', this.studiesNewRecord);
    this.loadFeatureTable(this.elevationsTable, this.elevationsFields, false, 'elevationsTableDiv', this.elevationsNewRecord);
    // Feature table listener
    this.featureTableListeners();
    this.elevationFeatureTableListeners();
  }

  // Create the feature table
  // Dependencies include (see ngInit for content):
  // 1. `tableName`: Feature Layer (table url, name, fields needed)
  // 2. `fieldNames`: Field names to display in the table
  // 3. `menuDisplay`: Display values for tables (Note: currently NOT leveraged)
  // 4. `tableDivName`: The div to place the table in
  // 5. `newTableFunctionSetup`: The functionality to add a new record
  // EXTRA: The `editingEnabled` property is a true/false value based on this.loggedIn value
  loadFeatureTable(tableName: any, fieldNames: Array<any>, menuDisplay: boolean, tableDivName: string, newTableFunctionSetup: any) {
    // Remove existing table content in <div>
    document.getElementById(tableDivName).textContent = '';
    // Load feature table
    const featureTable = new FeatureTable({
      view: this.view,
      layer: tableName,
      editingEnabled: this.loggedIn,
      fieldConfigs: fieldNames,
      container: document.getElementById(tableDivName),
      visibleElements: {
        menu: true,
        selectionColumn: true,
        menuItems: {
          clearSelection: false,
          refreshData: false,
          toggleColumns: false
        }
      },
      menuConfig: {
        items: newTableFunctionSetup as any
      }
    });
    // Load the feature table and name it to be accessed later
    // Send the feature table to the mapService so subscriptions can be set in other components (e.g., in modal dialogs via app.component.ts)
    if (tableDivName === 'studiesTableDiv') {
      this.studiesFeatureTable = featureTable;
      this.mapService.updateStudiesFeatureTable(this.studiesFeatureTable);
    } else if (tableDivName === 'elevationsTableDiv') {
      this.elevationsFeatureTable = featureTable;
      this.mapService.updateElevationsFeatureTable(this.elevationsFeatureTable);
    }
  }

  /* Study Feature Table Event Listener */
  featureTableListeners() {
    // Studies Feature Table OnChange
    this.studiesFeatureTable.on('selection-change', result => {
      // Remove existing basin graphics
      this.view.graphics.removeAll();
      /* If a user selects a row */
      if (result.added[0]) {
        // Grab the fs_id field of the selected record
        this.userSelectedID = result.added[0].feature.attributes.fs_id;
        // If selecting a new study
        // Check if a study was already selected AND if the currently selected record isn't the same as
        // If met, remove the previous study
        if (this.selectedRow && result.added[0].objectId !== this.selectedRow) {
          this.studiesFeatureTable.deselectRows(this.selectedRow);
          this.previouslySelectedRow = true;
        } else {
          this.previouslySelectedRow = false;
        }
        // Identify the currently selected table row
        this.selectedRow = result.added[0].objectId;
        // Capture the row properties for removing (used later)
        this.selectedRowProperties = result.added[0].feature;
        // Add the selected value to our user selected record
        this.userSelectedRecords.push(result.added[0].feature);
        // Re-load elevations properties and feature table
        this.elevationsTableProperties();
        this.loadFeatureTable(this.elevationsTable, this.elevationsFields, false, 'elevationsTableDiv', this.elevationsNewRecord);
        this.elevationFeatureTableListeners();
      /* Else if a user de-selects a row */
      } else if (result.removed[0]) {
        // If the user is un-checking the checkbox
        // This is verified if the captured properties match the existing this.userSelectedRecord array
        if (this.selectedRowProperties === this.userSelectedRecords[0]) {
          this.userSelectedID = null;
          this.userSelectedRecords = [];
          // Re-load elevations properties and feature table
          this.elevationsTableProperties();
          this.loadFeatureTable(this.elevationsTable, this.elevationsFields, false, 'elevationsTableDiv', this.elevationsNewRecord);
          this.elevationFeatureTableListeners();
        // Else if the user is selecting a new record from the table
        } else {
          // Set the record to the earlier captured properties
          this.userSelectedRecords = [this.selectedRowProperties];
        }
      }
    });
  }

    /* Elevation Feature Table Event Listener */
    elevationFeatureTableListeners() {
      this.elevationsSelected = [];
      // Elevations Feature Table OnChange
      this.elevationsFeatureTable.on('selection-change', result => {
        // Add the record to the array
        if (result.added[0]) {
          this.elevationsSelected.push(result.added[0].feature);
        // Else if a record is removed, only remove the record being removed from the array
        } else if (result.removed[0]) {
          for (let i = 0; i < this.elevationsSelected.length; i++) {
            if (this.elevationsSelected[i] === result.removed[0].feature) {
              this.elevationsSelected.splice(i, 1);
            }
          }
        }
        // If values reside in the array, set a boolean to true
        // Else set to false
        // Note: This is set for deleting records AND adding graphics to the map
        if (this.elevationsSelected.length > 0) {
          this.elevationSelectedBoolean = true;
        } else {
          this.elevationSelectedBoolean = false;
        }
        this.updateGraphicsToElevations();
      });
    }

    // Add elevation graphics to the map
    updateGraphicsToElevations() {
      // Remove existing graphics
      this.view.graphics.removeAll();
      // Clear the query string
      this.dowlknumQueryStringElevations = '';
      // If multiple records are in the elevation table, create an "OR" statement for the query string
      // Otherwise if 1 (ONE), or the last record in the table, end the statement
      for (let i = 0; i < this.elevationsSelected.length; i++) {
        if (i + 1 === this.elevationsSelected.length) {
          // tslint:disable-next-line: quotemark
          this.dowlknumQueryStringElevations += "dowlknum='" + this.elevationsSelected[i].attributes.dowlknum + "'";
        } else {
          // tslint:disable-next-line: quotemark
          this.dowlknumQueryStringElevations += "dowlknum='" + this.elevationsSelected[i].attributes.dowlknum + "' OR ";
        }
      }
      // If values are present, continue the selection
      // Else return
      if (this.dowlknumQueryStringElevations) {
        // Basins table view query
        const elevationQuery = this.queryBasinFeatureLayer.createQuery();
        elevationQuery.where = this.dowlknumQueryStringElevations;
        // Basin query features to create graphics layers
        this.queryBasinFeatureLayer.queryFeatures(elevationQuery).then(results => {
          // Return basin geometries as an array
          this.basinGeometriesElevations = results.features.map(feature => {
            return feature.geometry;
          });
          // Add the basin symbol and geometries to a graphic
          // Add the graphic to the map
          // tslint:disable-next-line: prefer-for-of
          for (let i = 0; i < this.basinGeometriesElevations.length; i++) {
            // Basin graphics layer
            const elevationGraphic = new Graphic({
              geometry: this.basinGeometriesElevations[i],
              symbol: this.basinSymbol as any,
            });
            this.view.graphics.add(elevationGraphic);
          }
        });
      } else {
        return;
      }
    }

  // Add new table record
  addNewTableRecord(tableName: string) {
    if (tableName === 'study') {
      // Close the feature table menu
      this.studiesFeatureTable.menu.open = false;
      this.openEditorWindow(AddStudyComponent);
    } else if (tableName === 'elevation') {
      // Close the feature table menu
      this.elevationsFeatureTable.menu.open = false;
      this.openEditorWindow(AddElevationComponent);
    }
  }

  // Remove table record
  removeTableRecord(tableName: string) {
    if (tableName === 'study') {
      if (this.userSelectedID) {
        this.studiesFeatureTable.menu.open = false; // Close the feature table menu
        this.tablesService.updateUserSelectedRecord(this.userSelectedRecords);
        this.openEditorWindow(AboutStudyDeleteComponent);
      } else {
        this.openEditorWindow(AboutDeleteComponent);
      }
    } else if (tableName === 'elevation') {
      this.elevationsFeatureTable.menu.open = false; // Close the feature table menu
      if (this.elevationSelectedBoolean) {
        this.tablesService.updateElevationsSelected(this.elevationsSelected);
        this.openEditorWindow(AboutElevationDeleteComponent);
      } else {
        this.openEditorWindow(AboutDeleteComponent);
      }
    }
  }

  // Open modal dialog to add a new record
  openEditorWindow(dialogName) {
    this.dialog.open(dialogName, {
      maxWidth: '100vw',
      width: '1000px',
      maxHeight: '90vh',
    });
  }

  // Search window for study table filtering
  // As the user adds content to the search window, update the table properties, then load the tables
  filterStudyTable(searchString: string) {
    // If a study is already selected and has graphics, reset the study to null and remove the map's graphics
    this.userSelectedID = null;
    this.view.graphics.removeAll();
    // Load feature table properties
    this.studiesTableProperties();
    this.elevationsTableProperties();
    // Load feature tables
    this.loadTables();
  }

}
